import { cloneElement, ReactElement, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { SimulatorTexts } from "@dd-report-components/report/ExecutiveSummaryCockpit";

type Values = Record<string, string | number>;

export const interpolate = (template: string, values?: Values) =>
  template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (values && name in values ? String(values[name]) : match));

// The locale files mark components as `<1>…</1>`; a report's own copy uses the
// same markup so one wording can be swapped for the other.
const renderTagged = (text: string, components?: Record<string, ReactElement>): ReactNode[] => {
  const parts: ReactNode[] = [];
  const tag = /<(\w+)>([\s\S]*?)<\/\1>/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = tag.exec(text))) {
    parts.push(text.slice(last, match.index));
    const component = components?.[match[1]];
    parts.push(component ? cloneElement(component, { key: match.index }, match[2]) : match[2]);
    last = match.index + match[0].length;
  }
  parts.push(text.slice(last));
  return parts;
};

// A simulator's copy: the report's own wording for a key when it brings one,
// the locale's otherwise.
export const useSimulatorText = (namespace: string, overrides?: SimulatorTexts) => {
  const { t } = useTranslation();
  const own = (key: string) => overrides?.[key];
  const text = (key: string, values?: Values): string => {
    const custom = own(key);
    return custom != null ? interpolate(custom, values) : t(`${namespace}.${key}`, values);
  };
  const rich = (key: string, values?: Values, components?: Record<string, ReactElement>): ReactNode => {
    const custom = own(key);
    if (custom == null) return <Trans i18nKey={`${namespace}.${key}`} values={values} components={components} />;
    return <>{renderTagged(interpolate(custom, values), components)}</>;
  };
  return { text, rich, has: (key: string) => own(key) != null };
};
